import { useState } from "react";
import { api } from "../api";
import { compactNumber } from "../format";
import type { Keyword, KeywordScope } from "../../shared/types";

export function KeywordsPanel({ keywords, onChanged }: { keywords: Keyword[]; onChanged: () => void }) {
  const [term, setTerm] = useState("");
  const [scope, setScope] = useState<KeywordScope>("fulltext");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add(event: React.FormEvent) {
    event.preventDefault();
    const value = term.trim();
    if (!value || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.addKeyword(value, scope);
      setTerm("");
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not add keyword");
    } finally {
      setBusy(false);
    }
  }

  async function remove(keyword: Keyword) {
    if (!confirm(`Stop monitoring "${keyword.term}" and drop its ${keyword.matchCount} match(es)?`)) return;
    setError(null);
    try {
      await api.deleteKeyword(keyword.id);
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not remove keyword");
    }
  }

  return (
    <div className="panel">
      <div className="panel-title">Keywords</div>
      <div className="panel-sub">Matched against the full paper, excluding its reference list</div>

      <form className="row-form" style={{ marginTop: 13 }} onSubmit={add}>
        <input
          className="input"
          value={term}
          placeholder="e.g. Shopee"
          maxLength={100}
          onChange={(event) => setTerm(event.target.value)}
        />
        <button className="btn btn-primary" type="submit" disabled={term.trim().length < 2 || busy}>
          Add
        </button>
      </form>

      <label
        style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 9, fontSize: 11.5, color: "var(--dim2)" }}
      >
        <input
          type="checkbox"
          checked={scope === "metadata"}
          onChange={(event) => setScope(event.target.checked ? "metadata" : "fulltext")}
        />
        Title, abstract and authors only — for broad terms that would match passing mentions
      </label>

      {error ? (
        <div className="error" style={{ marginTop: 9 }}>
          {error}
        </div>
      ) : null}

      <div className="list" style={{ marginTop: 13 }}>
        {keywords.length === 0 ? (
          <div className="empty">No keywords yet</div>
        ) : (
          keywords.map((keyword) => (
            <div className="list-row" key={keyword.id}>
              <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  className="dot"
                  style={{ background: keyword.active ? "var(--accent)" : "var(--dim2)" }}
                  title={keyword.active ? "Active" : "Paused"}
                />
                <span
                  className="mono"
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {keyword.term}
                </span>
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                <span className="mono" style={{ color: "var(--dim2)", fontSize: 11.5 }}>
                  {keyword.backfilledAt === null ? "scanning…" : compactNumber(keyword.matchCount)}
                </span>
                <button
                  className="btn btn-tiny"
                  type="button"
                  onClick={() =>
                    void api
                      .setKeywordScope(keyword.id, keyword.scope === "fulltext" ? "metadata" : "fulltext")
                      .then(onChanged)
                  }
                  title={
                    keyword.scope === "fulltext"
                      ? "Searching the full paper — click to restrict to metadata"
                      : "Searching metadata only — click to search the full paper"
                  }
                >
                  {keyword.scope === "fulltext" ? "full text" : "metadata"}
                </button>
                <button
                  className="btn btn-tiny"
                  type="button"
                  onClick={() => void api.setKeywordActive(keyword.id, !keyword.active).then(onChanged)}
                  title={keyword.active ? "Pause" : "Resume"}
                >
                  {keyword.active ? "pause" : "resume"}
                </button>
                <button className="btn btn-tiny" type="button" onClick={() => void remove(keyword)}>
                  ✕
                </button>
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
